'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

interface StatItem {
  value: string;
  label: string;
}

const stats: StatItem[] = [
  { value: '80+', label: 'Apps & Websites Delivered' },
  { value: '4.9×', label: 'Average Ads ROAS' },
  { value: '94%', label: 'Client Retention Rate' },
];

export default function HeroSection() {
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setStatsVisible(true), 600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-20">
      {/* Grid background */}
      <div className="absolute inset-0 grid-bg pointer-events-none" />

      {/* Glow blobs */}
      <div
        className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(245,166,35,0.12) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full pointer-events-none animate-blob animation-delay-4000"
        style={{
          background: 'radial-gradient(circle, rgba(245,166,35,0.08) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />
      <div
        className="absolute top-1/2 right-1/3 w-[400px] h-[400px] rounded-full pointer-events-none animate-blob-reverse animation-delay-2000"
        style={{
          background: 'radial-gradient(circle, rgba(100,100,255,0.06) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />
      <div className="absolute inset-0 noise-overlay pointer-events-none opacity-40" />

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 w-full">
        <div className="max-w-5xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-accent/20 bg-accent/5 mb-8 animate-fade-slide-in">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
            </span>
            <span className="text-xs font-700 text-accent tracking-widest uppercase">
              App Dev · Web Dev · Meta · TikTok · Google Ads
            </span>
          </div>

          {/* Heading */}
          <h1
            className="font-display font-800 text-foreground tracking-tight leading-none mb-6"
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(3rem, 8vw, 7rem)',
              lineHeight: 1.05,
            }}
          >
            Build Apps &amp;<br />
            <span className="text-gradient-amber">Websites That Win.</span>
            <br />
            <span style={{ opacity: 0.85 }}>Scale With Ads.</span>
          </h1>

          {/* Description */}
          <p className="text-lg md:text-xl text-muted font-400 leading-relaxed max-w-2xl mb-10">
            CampYellow designs and develops{' '}
            <span className="text-foreground font-600">mobile apps</span> and{' '}
            <span className="text-foreground font-600">high-performance websites</span>, then scales them with{' '}
            <span className="text-foreground font-600">Meta, TikTok &amp; Google Ads</span> — turning your digital product into a revenue machine.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <Link
              href="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-primary text-base font-700 rounded-full hover:bg-accent-light transition-all duration-300 hover:-translate-y-1 accent-glow"
              style={{ color: '#0d0d1a', fontWeight: 700 }}
            >
              Start Your Project
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
            <Link
              href="/about"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 glass-card text-foreground text-base font-600 rounded-full hover:border-accent/30 transition-all duration-300 hover:-translate-y-1"
            >
              Learn About Us
            </Link>
          </div>

          {/* Stats */}
          <div ref={statsRef} className="flex flex-wrap gap-8 md:gap-12">
            {stats.map((stat, i) => (
              <div
                key={stat.label}
                className="transition-all duration-700"
                style={{
                  transitionDelay: `${i * 150}ms`,
                  opacity: statsVisible ? 1 : 0,
                  transform: statsVisible ? 'translateY(0)' : 'translateY(1rem)',
                }}
              >
                <div
                  className="text-3xl md:text-4xl font-800 text-accent font-display tracking-tight"
                  style={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 800 }}
                >
                  {stat.value}
                </div>
                <div className="text-sm font-500 text-muted mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating card - App Launched */}
      <div className="absolute right-8 top-1/3 hidden xl:block animate-float">
        <div className="glass-card rounded-2xl p-4 w-52 border border-accent/10">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F5A623" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="2" />
                <line x1="12" y1="18" x2="12.01" y2="18" />
              </svg>
            </div>
            <span className="text-xs font-600 text-muted uppercase tracking-wider">App Launched</span>
          </div>
          <div
            className="text-2xl font-800 text-accent font-display"
            style={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 800 }}
          >
            iOS &amp; Android
          </div>
          <div className="text-xs text-muted mt-1">Cross-platform delivery</div>
        </div>
      </div>

      {/* Floating card - Ads ROAS */}
      <div className="absolute right-16 top-2/3 hidden xl:block animate-float-delayed">
        <div className="glass-card rounded-2xl p-4 w-48 border border-accent/10">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <span className="text-xs font-600 text-muted uppercase tracking-wider">Ads ROAS</span>
          </div>
          <div className="text-base font-700 text-foreground">4.9× Return</div>
          <div className="text-xs text-muted mt-1">Meta + TikTok + Google</div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-muted">
        <span className="text-xs font-500 tracking-widest uppercase">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-muted to-transparent" />
      </div>
    </section>
  );
}
